import { useState, useEffect, useRef } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import Icon from '../Icon';
import { ReactComponent as XmarkCircleIcon } from '../../../icons/xmark-circle.svg';
import './ArtistInput.scss';

const ArtistInput = ({ name, value, onChange, error, artists }) => {
  const { t } = useTranslation();
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredArtists, setFilteredArtists] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    // Filter artists based on input value
    if (value) {
      const filtered = artists.filter((artist) => artist.toLowerCase().includes(value.toLowerCase()));
      setFilteredArtists(filtered);
    } else {
      setFilteredArtists([]);
    }
  }, [value, artists]);

  useEffect(() => {
    // Click outside handler
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleInputChange = (e) => {
    onChange(e);
    setShowSuggestions(true);
    setSelectedIndex(-1);
  };

  const handleSuggestionClick = (artist) => {
    selectArtist(artist);
  };

  const selectArtist = (artist) => {
    const syntheticEvent = {
      currentTarget: {
        name,
        value: artist,
      },
    };
    onChange(syntheticEvent);
    setShowSuggestions(false);
    setSelectedIndex(-1);
  };

  const handleClear = () => {
    const fakeTarget = { name, value: '' };
    onChange({ target: fakeTarget, currentTarget: fakeTarget });
    setShowSuggestions(false);
    setSelectedIndex(-1);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (!showSuggestions || filteredArtists.length === 0) return;

    const incrementIndex = (prevIndex, increment) => {
      if (increment > 0) {
        return prevIndex < filteredArtists.length - 1 ? prevIndex + 1 : prevIndex;
      }
      return prevIndex > 0 ? prevIndex - 1 : 0;
    };

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((prevIndex) => incrementIndex(prevIndex, 1));
        break;
      case 'Tab':
        // Select highlighted item
        e.preventDefault();
        selectArtist(filteredArtists[selectedIndex >= 0 ? selectedIndex : 0]);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex((prevIndex) => incrementIndex(prevIndex, -1));
        break;
      case 'Enter':
        if (selectedIndex >= 0) {
          e.preventDefault();
          selectArtist(filteredArtists[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setSelectedIndex(-1);
        break;
      default:
        break;
    }
  };

  return (
    <div className="mg-form-group" ref={wrapperRef}>
      <div className="ArtistInput mg-input-clear-wrapper">
        <input
          ref={inputRef}
          type="text"
          name={name}
          className="mg-form-control"
          id={name}
          value={value}
          placeholder={t('songForm.artist')}
          onChange={handleInputChange}
          onFocus={() => setShowSuggestions(true)}
          onKeyDown={handleKeyDown}
        />
        {value && (
          <button
            type="button"
            className="mg-input-clear-btn"
            title={t('common.clear')}
            onMouseDown={(e) => e.preventDefault()}
            onClick={handleClear}
          >
            <Icon icon={<XmarkCircleIcon />} />
          </button>
        )}
        {showSuggestions && filteredArtists.length > 0 && (
          <ul className="artist-suggestions">
            {filteredArtists.map((artist, index) => {
              const suggestionClasses = classNames('artist-suggestion-item', {
                selected: index === selectedIndex,
              });

              return (
                <li key={index} onClick={() => handleSuggestionClick(artist)} className={suggestionClasses}>
                  {artist}
                </li>
              );
            })}
          </ul>
        )}
      </div>
      {error && <small className="mg-form-text mg-form-text--error">{error}</small>}
    </div>
  );
};

export default ArtistInput;
